import { Link } from 'react-router-dom';
import { formatMoney, type Assignment } from '@teentack/shared';
import { useMyAssignments } from '@/features/assignments/use-assignments';
import { useNotifications } from '@/features/notifications/use-notifications';
import { useReviews } from '@/features/reviews/use-reviews';
import { ReputationBadge } from '@/features/reviews/Stars';
import { useLocation } from '@/features/worksite/use-worksite';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { ArrowRightIcon, BellIcon, MapPinIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';

/**
 * What is happening right now, on the teen's own console.
 *
 * The dashboard already answered "what should I do next?". This answers "what
 * is going on?" — how employers rate them, what has just happened, and the
 * state of the job they are actually on, which is otherwise only visible by
 * opening My work.
 */

const STATUS_TONES: Record<string, BadgeTone> = {
  ASSIGNED: 'sun',
  ACCEPTED: 'brand',
  IN_PROGRESS: 'brand',
  COMPLETED: 'accent',
  APPROVED: 'safe',
};

function label(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');
}

/** The live state of one job, including a location if the teen is sharing. */
function ActiveJob({ assignment }: { assignment: Assignment }) {
  // Only polls while this card is on screen and the job is genuinely live.
  const live = ['ACCEPTED', 'IN_PROGRESS'].includes(assignment.status);
  const location = useLocation(assignment.id, live);

  return (
    <li className="rounded-xl border border-line bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-ink">{assignment.job.title}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {assignment.job.posterName} ·{' '}
            {formatMoney(assignment.agreedPay.amountMinor, assignment.agreedPay.currency)}
          </p>
        </div>
        <Badge tone={STATUS_TONES[assignment.status] ?? 'neutral'}>
          {label(assignment.status)}
        </Badge>
      </div>

      {location.data?.sharing && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-safe-700">
          <MapPinIcon className="size-3.5" />
          Sharing your location · {label(location.data.status)} ·{' '}
          {relativeTime(location.data.updatedAt ?? '')}
        </p>
      )}

      {assignment.status === 'ASSIGNED' && (
        <Link
          to="/my-work"
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline"
        >
          Accept or decline this
          <ArrowRightIcon className="size-3.5" />
        </Link>
      )}
    </li>
  );
}

export function TeenActivityPanel() {
  const reviews = useReviews({ pageSize: 50 });
  const recent = useNotifications({ pageSize: 5 });
  const assignments = useMyAssignments({ pageSize: 10 });

  const items = reviews.data?.items ?? [];
  const count = reviews.data?.total ?? 0;
  const average =
    items.length > 0
      ? Math.round((items.reduce((sum, review) => sum + review.rating, 0) / items.length) * 10) /
        10
      : null;

  const activeJobs = (assignments.data?.items ?? []).filter((assignment) =>
    ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED'].includes(assignment.status),
  );

  const notifications = recent.data?.items ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Standing */}
      <section className="rounded-2xl border border-line bg-white p-5 shadow-soft">
        <h2 className="text-base font-bold text-ink">How employers rate you</h2>
        <div className="mt-3">
          <ReputationBadge
            reputation={{ average, count, distribution: [0, 0, 0, 0, 0] }}
            size="md"
          />
        </div>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          {count === 0
            ? 'Your first rating arrives once an employer approves a job you have finished.'
            : `Across ${count} finished ${count === 1 ? 'job' : 'jobs'}. Employers see this on your applications.`}
        </p>
        <Link
          to="/profile"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-600 hover:underline"
        >
          See your reviews
          <ArrowRightIcon className="size-4" />
        </Link>
      </section>

      {/* Recent activity */}
      <section className="rounded-2xl border border-line bg-white p-5 shadow-soft">
        <div className="flex items-center justify-between gap-3">
          <h2 className="inline-flex items-center gap-2 text-base font-bold text-ink">
            <BellIcon className="size-4 text-slate-400" />
            Just happened
          </h2>
          <Link
            to="/notifications"
            className="text-xs font-semibold text-brand-600 hover:underline"
          >
            See all
          </Link>
        </div>

        {notifications.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            Nothing yet. We will tell you the moment something changes.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {notifications.map((item) => (
              <li key={item.id} className="flex items-start gap-2">
                {!item.isRead && (
                  <span
                    aria-hidden="true"
                    className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-600"
                  />
                )}
                <div className={item.isRead ? 'min-w-0 pl-3.5' : 'min-w-0'}>
                  <p className="truncate text-sm font-semibold text-ink">{item.title}</p>
                  <p className="text-xs text-slate-400">{relativeTime(item.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Work in flight — renders nothing when there is none. */}
      {activeJobs.length > 0 && (
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-base font-bold text-ink">Your jobs right now</h2>
          <ul className="space-y-2">
            {activeJobs.map((assignment) => (
              <ActiveJob key={assignment.id} assignment={assignment} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
